import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";

import { useEffect, useState } from "react";

const GET_REOPEN_COHORT_PREVIEW = gql(`
  query GetReopenCohortPreview($cohortId: ID!) {
    reopenCohortPreview(cohortId: $cohortId) {
      sourceCohortId
      sourceCohort {
        id
        type
        status
        reportingYear
        reportKind
        state
        tenantId
        tenant {
          id
          name
        }
        partnerTenantId
        fdId
        lepcId
        credentialUsername
        alternateIdValue
      }
      count
      items {
        facilityId
        tierIIReportId
        previousStatus
        status
        facility {
          id
          name
          city
          state
        }
      }
      removedCount
      removedItems {
        facilityId
        previousStatus
        facility {
          id
          name
          city
          state
        }
      }
    }
  }
`);

const REOPEN_COHORT = gql(`
  mutation ReopenCohort($cohortId: ID!) {
    reopenCohort(cohortId: $cohortId) {
      cohort {
        id
      }
      newMemberCount
    }
  }
`);

interface ReopenCohortDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCohortId: string;
  canExecuteReopen?: boolean;
  onSuccess?: () => void;
}

export function ReopenCohortDialog({
  open,
  onClose,
  sourceCohortId,
  canExecuteReopen = false,
  onSuccess,
}: ReopenCohortDialogProps) {
  const alerts = useAlerts();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) {
      setAcknowledged(false);
    }
  }, [open]);

  const { data, loading, error } = useQuery(GET_REOPEN_COHORT_PREVIEW, {
    variables: { cohortId: sourceCohortId },
    skip: !open || !sourceCohortId,
    fetchPolicy: "network-only",
    onError: (error) => {
      alerts.error(`Failed to load reopen preview: ${error.message}`);
    },
  });

  const [doReopenCohort, { loading: reopening }] = useMutation(REOPEN_COHORT, {
    refetchQueries: ["GetTierIIReport", "GetCohort"],
    onCompleted: () => {
      alerts.success("Cohort reopened successfully");
      onClose();
      onSuccess?.();
    },
    onError: (error) => {
      alerts.error(`Failed to reopen cohort: ${error.message}`);
    },
  });

  const previewResult = data?.reopenCohortPreview;
  const sourceCohort = previewResult?.sourceCohort;
  const newMembers = previewResult?.items ?? [];
  const newCount = previewResult?.count ?? 0;
  const removedMembers = previewResult?.removedItems ?? [];
  const removedCount = previewResult?.removedCount ?? 0;
  const hasPreview = Boolean(previewResult);
  const hasChanges = hasPreview && (newCount > 0 || removedCount > 0);
  const isConfirmDisabled =
    loading ||
    reopening ||
    Boolean(error) ||
    !hasPreview ||
    !acknowledged ||
    !canExecuteReopen;

  let confirmButtonLabel = "Reopen Cohort";
  if (error || !hasPreview) {
    confirmButtonLabel = "Preview Unavailable";
  } else if (!canExecuteReopen) {
    confirmButtonLabel = "Not Authorized";
  } else if (!acknowledged) {
    confirmButtonLabel = "Acknowledge to Continue";
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Confirm Reopen Cohort</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2">Loading reopen preview...</Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Reopening this cohort will cancel the current workflow and all its
              activities, set the cohort status back to OPEN, and synchronize
              membership to include newly arrived members.
            </Typography>

            {sourceCohort && (
              <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: "#e3f2fd" }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Cohort Will Be Reopened
                </Typography>
                <Typography variant="body2">
                  Current Status: {sourceCohort.status} (will become OPEN)
                </Typography>
                <Typography variant="body2">
                  Type: {prettyPrintEnumValue(sourceCohort.type)}
                </Typography>
                <Typography variant="body2">
                  Reporting Year: {sourceCohort.reportingYear}
                </Typography>
                <Typography variant="body2">
                  State: {sourceCohort.state}
                </Typography>
                <Typography variant="body2">
                  Tenant:{" "}
                  {sourceCohort.tenant?.name
                    ? `${sourceCohort.tenant.name} (${sourceCohort.tenant.id})`
                    : sourceCohort.tenantId}
                </Typography>
                {sourceCohort.partnerTenantId && (
                  <Typography variant="body2">
                    Partner Tenant ID: {sourceCohort.partnerTenantId}
                  </Typography>
                )}
                {sourceCohort.fdId && (
                  <Typography variant="body2">
                    Fire Department ID: {sourceCohort.fdId}
                  </Typography>
                )}
                {sourceCohort.lepcId && (
                  <Typography variant="body2">
                    LEPC ID: {sourceCohort.lepcId}
                  </Typography>
                )}
                {sourceCohort.credentialUsername && (
                  <Typography variant="body2">
                    Credential Username: {sourceCohort.credentialUsername}
                  </Typography>
                )}
                {sourceCohort.alternateIdValue && (
                  <Typography variant="body2">
                    Alternate ID Value: {sourceCohort.alternateIdValue}
                  </Typography>
                )}
              </Box>
            )}

            {error || !hasPreview ? (
              <Alert severity="error">
                Reopen preview is unavailable for this cohort in its current
                state.
              </Alert>
            ) : !canExecuteReopen ? (
              <Alert severity="error">
                You are not authorized to perform this action. Reopening a
                cohort is restricted to users who can reset reports.
              </Alert>
            ) : !hasChanges ? (
              <Alert severity="info">
                No membership changes were found. You can still reopen this
                cohort to update/re-snapshot members.
              </Alert>
            ) : (
              <Alert severity="warning">
                This action will cancel the current cohort workflow and all its
                activities. The cohort will need to go through its workflow
                again from the beginning after membership is synchronized.
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  disabled={
                    loading ||
                    reopening ||
                    Boolean(error) ||
                    !hasPreview ||
                    !canExecuteReopen
                  }
                />
              }
              label="I understand this will cancel the current workflow and reopen the cohort."
            />

            {newCount > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Newly Eligible Members ({newCount})
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Previous Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          New Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {newMembers.map((item) => (
                        <TableRow key={item.facilityId}>
                          <TableCell>
                            {item.facility?.name ?? item.facilityId}
                          </TableCell>
                          <TableCell>
                            {item.facility?.city && item.facility?.state
                              ? `${item.facility.city}, ${item.facility.state}`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {item.previousStatus
                              ? prettyPrintEnumValue(item.previousStatus)
                              : "New"}
                          </TableCell>
                          <TableCell>
                            {prettyPrintEnumValue(item.status)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {removedCount > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }} color="error">
                  Members to Be Removed ({removedCount})
                </Typography>
                <Alert severity="error" sx={{ mb: 1 }}>
                  These members are no longer eligible and will be removed from
                  the cohort when it is reopened.
                </Alert>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Current Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          After Reopen
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {removedMembers.map((item) => (
                        <TableRow key={item.facilityId}>
                          <TableCell>
                            {item.facility?.name ?? item.facilityId}
                          </TableCell>
                          <TableCell>
                            {item.facility?.city && item.facility?.state
                              ? `${item.facility.city}, ${item.facility.state}`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {item.previousStatus
                              ? prettyPrintEnumValue(item.previousStatus)
                              : "Unknown"}
                          </TableCell>
                          <TableCell>Removed — no longer eligible</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={reopening}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            doReopenCohort({ variables: { cohortId: sourceCohortId } })
          }
          disabled={isConfirmDisabled}
        >
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
